import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import { opcoesDosContratosParaEscolha } from '../../shared/api/contratosParaEscolha';
import { formatarValorEmReais } from '../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { TrechoDoContrato } from '../../shared/ui/TrechoDoContrato';
import { opcoesDaFila } from './api/glosas';
import { diaDoPrazoEm, diasAteOPrazo, formatarDataDoPrazo, itemDaGlosa, tipoVigente } from './api/mapeadores';
import {
  AREAS_DE_GLOSA,
  ROTULOS_DE_AREA,
  ROTULOS_DE_CONFIANCA,
  ROTULOS_DE_MOTIVO,
  ROTULOS_DE_TIPO,
  TIPOS_DE_GLOSA,
  type AreaDeGlosa,
  type Confianca,
  type FiltroDaFila,
  type Glosa,
  type TipoDeGlosa,
  type TrechoCitado,
} from './api/tipos';
import { DecisaoDaGlosa } from './DecisaoDaGlosa';
import { ImportarDemonstrativo } from './ImportarDemonstrativo';

const FILA_DE_REVISAO: FiltroDaFila = {
  confianca: 'baixa',
  tipo: '',
  situacao: 'pendentes',
  demonstrativoId: null,
  area: '',
  hipoteseRegraId: null,
};

/** O estado do ticket da área, como a tela o diz (`ticket.estado`). Token novo aparece cru. */
const ROTULOS_DO_ESTADO_DO_TICKET: Readonly<Record<string, string>> = {
  aberto: 'aberto',
  em_andamento: 'em andamento',
  aguardando_terceiro: 'aguardando terceiro',
  concluido: 'concluído',
  cancelado: 'cancelado',
};

/** Vindo da tela de Contratos (`?hipoteseRegraId=`): todas as glosas que citaram a hipótese, de qualquer confiança. */
function filtroInicial(hipoteseRegraId: string | null): FiltroDaFila {
  return hipoteseRegraId !== null && hipoteseRegraId !== ''
    ? { confianca: 'todas', tipo: '', situacao: 'todas', demonstrativoId: null, area: '', hipoteseRegraId }
    : FILA_DE_REVISAO;
}

/** Prazo a 7 dias ou menos pede atenção; vencido é estourado. Mesmos tons de `palette.sla`. */
const DIAS_DE_ATENCAO = 7;

const TOM_DA_CONFIANCA: Record<Confianca, 'ok' | 'atencao' | 'estourado' | 'semPrazo'> = {
  alta: 'ok',
  media: 'atencao',
  baixa: 'estourado',
  desconhecido: 'semPrazo',
};

function SeloDeConfianca({ confianca }: { confianca: Confianca }): React.JSX.Element {
  return (
    <Chip
      size="small"
      label={`Confiança ${ROTULOS_DE_CONFIANCA[confianca].toLowerCase()}`}
      sx={(tema) => {
        const tom = tema.palette.sla[TOM_DA_CONFIANCA[confianca]];
        return { backgroundColor: tom.fundo, color: tom.tinta, border: `1px solid ${tom.borda}`, fontWeight: 600, height: 20 };
      }}
    />
  );
}

function CelulaDaGuia({ glosa }: { glosa: Glosa }): React.JSX.Element {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {glosa.guia}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {itemDaGlosa(glosa)}
        {glosa.protocolo !== null ? ` · protocolo ${glosa.protocolo}` : ''}
      </Typography>
    </Stack>
  );
}

function CelulaDoTipo({ glosa }: { glosa: Glosa }): React.JSX.Element {
  const vigente = tipoVigente(glosa);
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {ROTULOS_DE_TIPO[vigente]}
      </Typography>
      <SeloDeConfianca confianca={glosa.confianca} />
      {vigente !== glosa.tipo ? (
        <Typography variant="caption" color="text.secondary">
          o classificador deu {ROTULOS_DE_TIPO[glosa.tipo]}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** De onde saiu a causa, na ordem `D7`: código da operadora, verificação do motor, ou nenhuma (linear). */
function origemDaCausa(glosa: Glosa): string {
  switch (glosa.origemDaCausa) {
    case 'codigo_operadora':
      return `pelo código da operadora${glosa.motivoCodigo !== null ? ` ${glosa.motivoCodigo}` : ''}`;
    case 'verificacao_motor':
      return `pela verificação ${glosa.verificacao !== null ? String(glosa.verificacao) : '?'} do motor`;
    case 'sem_causa':
      return glosa.motivoCodigo !== null
        ? `o código ${glosa.motivoCodigo} da operadora não está no mapa e o motor não apontou causa: vira linear`
        : 'sem código da operadora e sem causa apontada pelo motor: vira linear';
    default:
      return 'origem desconhecida';
  }
}

function CelulaDaCausa({ glosa }: { glosa: Glosa }): React.JSX.Element {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 240, maxWidth: 380 }}>
      <Typography variant="body2">{glosa.causa ?? 'Sem causa'}</Typography>
      <Typography variant="caption" color="text.secondary">
        {origemDaCausa(glosa)}
      </Typography>
      {glosa.clausula !== null ? (
        <TrechoDoContrato trecho={glosa.clausula.trecho} pagina={glosa.clausula.pagina} />
      ) : glosa.causa !== null ? (
        <Typography variant="caption" color="text.secondary">
          O contrato não tem cláusula para esta causa: vale o tipo padrão dela.
        </Typography>
      ) : null}
      {glosa.regraConferida !== null ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Regra que o motor conferiu na linha cobrada:
          </Typography>
          <TrechoDoContrato
            trecho={glosa.regraConferida.trecho}
            pagina={glosa.regraConferida.pagina}
            rotulo="Regra conferida pelo motor"
          />
        </>
      ) : null}
    </Stack>
  );
}

function CelulaDoPrazo({ prazo, clausula }: { prazo: string | null; clausula: TrechoCitado | null }): React.JSX.Element {
  if (prazo === null) {
    return (
      <Stack spacing={0.25} sx={{ maxWidth: 160 }}>
        <Typography variant="body2">Sem prazo</Typography>
        <Typography variant="caption" color="text.secondary">
          falta a data de pagamento ou a cláusula de recurso
        </Typography>
      </Stack>
    );
  }
  const dias = diasAteOPrazo(prazo);
  const legenda =
    dias === null ? null : dias < 0 ? `vencido há ${String(-dias)} dias` : dias === 0 ? 'vence hoje' : `faltam ${String(dias)} dias`;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {formatarDataDoPrazo(prazo)}
      </Typography>
      {legenda !== null ? (
        <Typography
          variant="caption"
          sx={(tema) => ({
            whiteSpace: 'nowrap',
            color:
              dias !== null && dias < 0
                ? tema.palette.sla.estourado.tinta
                : dias !== null && dias <= DIAS_DE_ATENCAO
                  ? tema.palette.sla.atencao.tinta
                  : tema.palette.text.secondary,
            fontWeight: dias !== null && dias <= DIAS_DE_ATENCAO ? 600 : 400,
          })}
        >
          {legenda}
        </Typography>
      ) : null}
      {clausula !== null ? (
        <Box sx={{ maxWidth: 260 }}>
          <TrechoDoContrato trecho={clausula.trecho} pagina={clausula.pagina} rotulo="Cláusula do prazo de recurso" />
        </Box>
      ) : null}
    </Stack>
  );
}

const SEM_ROTEAMENTO = (
  <Typography variant="caption" color="text.secondary">
    —
  </Typography>
);

/**
 * Para onde foi a glosa técnica (`TP-070`, `NR-249`): o ticket da área e o prazo dele, ou por que não foi. Só a linha de
 * tipo vigente técnico tem roteamento a mostrar; as outras ficam com um traço (têm outro destino: recurso, tabela).
 * O ticket que já existe aparece mesmo que o tipo tenha mudado depois, para ninguém achar que ele sumiu.
 */
function CelulaDoRoteamento({ glosa }: { glosa: Glosa }): React.JSX.Element {
  const roteamento = glosa.roteamento;
  const vigente = tipoVigente(glosa);
  if (roteamento === null) return SEM_ROTEAMENTO;

  if (roteamento.situacao === 'roteada') {
    const dia = roteamento.prazoEm !== null ? diaDoPrazoEm(roteamento.prazoEm) : null;
    const estado = ROTULOS_DO_ESTADO_DO_TICKET[roteamento.ticketEstado] ?? roteamento.ticketEstado;
    const mudou =
      roteamento.ticketEstado === 'cancelado'
        ? 'ticket cancelado: o tipo da glosa mudou'
        : vigente !== 'tecnica'
          ? `o tipo da glosa mudou para ${ROTULOS_DE_TIPO[vigente].toLowerCase()}; o ticket segue ${estado}`
          : null;
    return (
      <Stack spacing={0.25} sx={{ minWidth: 200, maxWidth: 280 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Roteada para {ROTULOS_DE_AREA[roteamento.area]}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dia !== null ? `prazo ${formatarDataDoPrazo(dia)}` : 'sem prazo: falta a data de pagamento'}
          {mudou === null ? ` · ticket ${estado}` : ''}
        </Typography>
        {mudou !== null ? (
          <Typography variant="caption" color="text.secondary">
            {mudou}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  if (vigente !== 'tecnica') return SEM_ROTEAMENTO;

  if (roteamento.situacao === 'nao_roteada') {
    return (
      <Stack spacing={0.25} sx={{ minWidth: 200, maxWidth: 280 }}>
        <Typography variant="body2">Não roteada</Typography>
        <Typography variant="caption" color="text.secondary">
          {ROTULOS_DE_MOTIVO[roteamento.motivo]}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25} sx={{ minWidth: 200, maxWidth: 280 }}>
      <Typography variant="body2">Ticket ainda não aberto</Typography>
      <Typography variant="caption" color="text.secondary">
        a área o recebe na próxima importação ou decisão
      </Typography>
    </Stack>
  );
}

/**
 * A glosa classificada a partir do contrato (`TP-054`, T11, `NR-248`). O
 * demonstrativo entra por importação; cada linha glosada chega com causa,
 * tipo, confiança, a cláusula do contrato que decidiu o tipo e o prazo do
 * recurso. A fila padrão é a de confiança baixa — o que uma pessoa precisa
 * olhar —, por isso vem antes da importação. A classificação é
 * determinística: a IA só entra antes, no compilador, lendo as cláusulas de
 * glosa do contrato (que passam pela revisão de regras como qualquer outra).
 *
 * Rascunho de recurso e protocolo de envio ainda não existem e não aparecem
 * aqui.
 */
export function PaginaDeGlosas(): React.JSX.Element {
  const navegar = useNavigate();
  const consultaDosContratos = useQuery(opcoesDosContratosParaEscolha());
  const [parametros, definirParametros] = useSearchParams();
  const [contratoId, definirContratoId] = useState('');
  const [filtro, definirFiltroLocal] = useState<FiltroDaFila>(() => filtroInicial(parametros.get('hipoteseRegraId')));
  // Sair do filtro da hipótese tira o parâmetro da URL: recarregar a página não pode voltar a ele.
  const definirFiltro = (proximo: FiltroDaFila | ((atual: FiltroDaFila) => FiltroDaFila)): void => {
    definirFiltroLocal((atual) => {
      const novo = typeof proximo === 'function' ? proximo(atual) : proximo;
      if (novo.hipoteseRegraId === null && parametros.has('hipoteseRegraId')) {
        definirParametros(
          (atuais) => {
            const seguintes = new URLSearchParams(atuais);
            seguintes.delete('hipoteseRegraId');
            return seguintes;
          },
          { replace: true },
        );
      }
      return novo;
    });
  };

  const contratos = useMemo(() => consultaDosContratos.data ?? [], [consultaDosContratos.data]);

  useEffect(() => {
    if (consultaDosContratos.error instanceof ErroDaApi && consultaDosContratos.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
  }, [consultaDosContratos.error, navegar]);

  const consultaDaFila = useQuery(opcoesDaFila(filtro));

  const tituloDaLista =
    filtro.hipoteseRegraId !== null
      ? 'Glosas que citaram a hipótese do contrato'
      : filtro.demonstrativoId !== null
        ? 'Glosas do demonstrativo importado'
        : 'Fila de glosas';
  const filtroAmplo = filtro.demonstrativoId !== null || filtro.hipoteseRegraId !== null || filtro.area !== '';

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Glosa e recurso</Typography>
        <Typography variant="body2" color="text.secondary">
          Cada linha glosada do demonstrativo é classificada pela causa e pelas cláusulas de glosa
          aprovadas no contrato: tipo, confiança e o prazo para recorrer. A fila mostra, por padrão,
          só a confiança baixa — o que precisa de uma pessoa. Confirmar ou corrigir o tipo nunca
          apaga a classificação original.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="h2">{tituloDaLista}</Typography>

          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems="center">
            <TextField
              select
              label="Confiança"
              size="small"
              value={filtro.confianca}
              onChange={(evento) => {
                definirFiltro((atual) => ({ ...atual, confianca: evento.target.value as FiltroDaFila['confianca'] }));
              }}
              sx={{ minWidth: 200 }}
            >
              {filtroAmplo ? <MenuItem value="todas">Todas</MenuItem> : null}
              <MenuItem value="baixa">Baixa (fila de revisão)</MenuItem>
              <MenuItem value="media">Média</MenuItem>
              <MenuItem value="alta">Alta</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo"
              size="small"
              value={filtro.tipo}
              onChange={(evento) => {
                definirFiltro((atual) => ({ ...atual, tipo: evento.target.value as TipoDeGlosa | '' }));
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos os tipos</MenuItem>
              {TIPOS_DE_GLOSA.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>
                  {ROTULOS_DE_TIPO[tipo]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Área"
              size="small"
              value={filtro.area}
              onChange={(evento) => {
                const area = evento.target.value as AreaDeGlosa | '';
                definirFiltro((atual) => ({
                  ...atual,
                  area,
                  // Escolher uma área é olhar o que ela recebeu, em qualquer confiança; sem área e sem demonstrativo, a API
                  // só serve a confiança baixa.
                  confianca:
                    area !== '' && atual.confianca === 'baixa'
                      ? 'todas'
                      : area === '' && atual.demonstrativoId === null && atual.confianca === 'todas'
                        ? 'baixa'
                        : atual.confianca,
                  // O que a área recebeu inclui o que já foi decidido; voltar à fila volta às pendentes.
                  situacao: area !== '' ? 'todas' : atual.demonstrativoId === null ? 'pendentes' : atual.situacao,
                }));
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todas as áreas</MenuItem>
              {AREAS_DE_GLOSA.map((area) => (
                <MenuItem key={area} value={area}>
                  {ROTULOS_DE_AREA[area]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Decisão"
              size="small"
              value={filtro.situacao}
              onChange={(evento) => {
                definirFiltro((atual) => ({ ...atual, situacao: evento.target.value as FiltroDaFila['situacao'] }));
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="pendentes">Sem decisão</MenuItem>
              <MenuItem value="todas">Com e sem decisão</MenuItem>
            </TextField>

            {filtroAmplo ? (
              <Button
                size="small"
                onClick={() => {
                  definirFiltro(FILA_DE_REVISAO);
                }}
              >
                Voltar para a fila de revisão
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Junta os demonstrativos de todos os contratos.
              </Typography>
            )}
          </Stack>

          {consultaDaFila.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

          {consultaDaFila.isError ? (
            <EstadoDeErro
              erro={consultaDaFila.error}
              contexto="Não foi possível carregar as glosas"
              aoReprocessar={() => {
                void consultaDaFila.refetch();
              }}
            />
          ) : null}

          {consultaDaFila.isSuccess && consultaDaFila.data.length === 0 ? (
            <EstadoVazio
              titulo="Nenhuma glosa neste filtro"
              descricao={
                filtro.hipoteseRegraId !== null && filtro.confianca === 'todas' && filtro.tipo === ''
                  ? 'Nenhum demonstrativo importado tem glosa que cite esta hipótese.'
                  : !filtroAmplo && filtro.confianca === 'baixa'
                    ? 'Nada com confiança baixa esperando revisão.'
                    : 'Troque o filtro de confiança, de tipo ou de área para ver outras glosas.'
              }
            />
          ) : null}

          {consultaDaFila.isSuccess && consultaDaFila.data.length > 0 ? (
            <TableContainer>
              <Table aria-label="Glosas classificadas" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Guia</TableCell>
                    <TableCell align="right">Valor glosado</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Causa e cláusula do contrato</TableCell>
                    <TableCell>Prazo do recurso</TableCell>
                    <TableCell>Roteamento</TableCell>
                    <TableCell>Decisão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultaDaFila.data.map((glosa) => (
                    <TableRow key={glosa.id} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell>
                        <CelulaDaGuia glosa={glosa} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatarValorEmReais(glosa.valorGlosado)}
                      </TableCell>
                      <TableCell>
                        <CelulaDoTipo glosa={glosa} />
                      </TableCell>
                      <TableCell>
                        <CelulaDaCausa glosa={glosa} />
                      </TableCell>
                      <TableCell>
                        <CelulaDoPrazo prazo={glosa.prazoRecursoAte} clausula={glosa.clausulaDoPrazo} />
                      </TableCell>
                      <TableCell>
                        <CelulaDoRoteamento glosa={glosa} />
                      </TableCell>
                      <TableCell>
                        <DecisaoDaGlosa glosa={glosa} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <ImportarDemonstrativo
          contratoId={contratoId}
          seletorDeContrato={
            <TextField
              select
              label="Contrato"
              size="small"
              value={contratoId}
              onChange={(evento) => {
                definirContratoId(evento.target.value);
              }}
              sx={{ width: 360, maxWidth: '100%' }}
            >
              <MenuItem value="">Escolha um contrato</MenuItem>
              {contratos.map((contrato) => (
                <MenuItem key={contrato.id} value={contrato.id}>
                  {contrato.rotulo}
                </MenuItem>
              ))}
            </TextField>
          }
          aoImportar={(demonstrativo) => {
            definirFiltro({
              confianca: 'todas',
              tipo: '',
              situacao: 'todas',
              demonstrativoId: demonstrativo.demonstrativoId,
              area: '',
              hipoteseRegraId: null,
            });
          }}
        />
      </Paper>
    </Stack>
  );
}
